import React, { useEffect, useMemo, useState } from "react";

import HttpManager from "../../../../services/HttpManager";
import { InvitationPreviewDTO, WorkshopAttendeeDTO } from "../../../../models/WorkshopDTO";
import S from "./strings";
import { BotonX, BuscarAlumna, EstadoPill, Opciones, PromptModal, dinero, dineroCod, errorText, useRolDinero } from "./comun";
import { horasEntre, horasTexto } from "./dias";
import { cursoCorto } from "../../../../utils/cursoCorto";
import { useNavigate } from "react-router-dom";
import { WorkshopMultiDetailDTO } from "../../../../services/v2/workshops.service";
import { fechaHoraAmPm } from "../../../../utils/fechaHoraAmPm";
import { ChipsSesiones, Sesiones } from "./sesiones";
import WorkshopForm from "./workshopForm";
import { BotonWhatsappAlumno, useVeWappChat } from "../../../../components/whatsapp/whatsappAlumno";
import EnvioMasivo from "../../../../components/whatsapp/envioMasivo";

interface Props {
	campusId: number;
	id: number;
	opciones: Opciones;
	onBack: () => void;
	notify: (text: string, ok?: boolean) => void;
}


/**
 * Invitación por WhatsApp (v2, 2026-10-23): destinatarias por curso (los del taller de entrada) y el envío con una plantilla
 * aprobada de WhatsApp; fecha, hora, taller, precio y plantel se le ponen solos a la plantilla.
 */
const Invitacion: React.FC<{ campusId: number; taller: WorkshopMultiDetailDTO; opciones: Opciones; notify: Props["notify"]; onInscritas: () => void }> = ({ campusId, taller, opciones, notify, onInscritas }) => {
	const http = HttpManager.getInstance();
	const navigate = useNavigate();
	const [cobra, setCobra] = useState<boolean>((taller.price || 0) > 0);
	const [cursos, setCursos] = useState<number[]>(() => (taller.courses || []).map(c => c.id).concat(!taller.courses?.length && taller.courseId ? [taller.courseId] : []));
	const [vista, setVista] = useState<InvitationPreviewDTO | null>(null);
	const [quitar, setQuitar] = useState<number[]>([]);
	const [busy, setBusy] = useState<boolean>(false);
	const [masivo, setMasivo] = useState<boolean>(false);
	const veWa = useVeWappChat();
	const lista = [...opciones.cursos];
	(taller.courses || []).forEach(c => { if (!lista.some(x => x.id === c.id)) lista.unshift({ id: c.id, name: cursoCorto(c.name) || c.name }); });

	const ver = () => {
		if (cursos.length === 0) { notify(S.elegirCurso, false); return; }
		setBusy(true);
		http.workshopsService.invitationPreview(campusId, taller.id, cursos)
			.then(r => { setVista(r.data); setQuitar(r.data.recipients.filter(x => x.alreadyEnrolled).map(x => x.studentId)); })
			.catch(e => notify(errorText(e), false))
			.finally(() => setBusy(false));
	};
	const elegidas = (vista?.recipients || []).filter(r => !quitar.includes(r.studentId));
	const porInscribir = elegidas.filter(r => !r.alreadyEnrolled);
	// R2: inscribir de una vez a las destinatarias elegidas (las ya inscritas no cuentan)
	const inscribir = () => {
		setBusy(true);
		http.workshopsService.addAttendeesBulk(campusId, taller.id, porInscribir.map(r => r.studentId), cobra)
			.then(r => {
				const s = (r.data.skipped || []).length;
				notify(S.inscritasOk(r.data.added, s) + (s ? " " + r.data.skipped.map(x => x.reason).filter((x, i, xs) => xs.indexOf(x) === i).join(" ") : ""), s === 0);
				onInscritas();
				setVista(v => v ? { ...v, recipients: v.recipients.map(x => porInscribir.some(p => p.studentId === x.studentId) && !r.data.skipped.some(k => k.studentId === x.studentId) ? { ...x, alreadyEnrolled: true } : x) } : v);
			})
			.catch(e => notify(errorText(e), false))
			.finally(() => setBusy(false));
	};
	const d = vista?.data;
	// Los datos que aceptan las plantillas (PlantillasService::VARS): fecha, horarios, precio, monto y dato (= nombre del taller)
	const fijos: Record<string, string> = d ? { fecha: d.fecha, horarios: d.hora, precio: d.precio, monto: d.precio, dato: d.taller } : {};

	return (
		<div className="wau-caja">
			<div className="wte-cab-caja">
				<h3>{S.invitacion}</h3>
				<button type="button" className="wau-btn s mini" onClick={() => navigate("/plantillas")}>{S.crearPlantilla}</button>
			</div>
			<span className="wau-lab">{S.cursosDestino}</span>
			<div className="wte-cursos" role="group" style={{ marginTop: 4, marginBottom: 8 }}>
				{lista.map(c => (
					<button key={c.id} type="button" className="wau-chip" aria-pressed={cursos.includes(c.id)}
						onClick={() => { setVista(null); setCursos(cs => cs.includes(c.id) ? cs.filter(x => x !== c.id) : [...cs, c.id]); }}>{c.name}</button>
				))}
			</div>
			<div className="wau-acciones" style={{ justifyContent: "flex-start", marginTop: 0 }}>
				<button type="button" className="wau-btn s" disabled={busy || cursos.length === 0} onClick={ver}>{S.verDestinatarias}</button>
				{vista ? <span className="wau-sec">{S.destinatarias(vista.total)}</span> : null}
				{vista && veWa && elegidas.length > 0 ? <button type="button" className="wau-btn p" disabled={busy} onClick={() => setMasivo(true)}>{S.enviarInvitacion(elegidas.length)}</button> : null}
				{vista && porInscribir.length > 0 ? <>
					<label className="wau-campo" style={{ flexDirection: "row", alignItems: "center", gap: 6 }}><input type="checkbox" checked={cobra} onChange={e => setCobra(e.target.checked)} /><span>{S.seCobra}</span></label>
					<button type="button" className="wau-btn s" disabled={busy} onClick={inscribir}>{S.inscribirN(porInscribir.length)}</button>
				</> : null}
			</div>
			{!vista ? null : (
				<div className="wau-tabla" style={{ marginTop: 10, maxHeight: 420, overflowY: "auto" }}>
					<table>
						<thead><tr><th style={{ width: 36 }}></th><th>{S.colAlumna}</th><th>{S.curso}</th><th>{S.colTel}</th><th style={{ width: 56 }}></th></tr></thead>
						<tbody>
							{vista.recipients.map(r => (
								<tr key={r.studentId + "_" + r.enrollmentId}>
									<td><input type="checkbox" checked={!quitar.includes(r.studentId)} aria-label={r.name}
										onChange={() => setQuitar(q => q.includes(r.studentId) ? q.filter(x => x !== r.studentId) : [...q, r.studentId])} /></td>
									<td><b>{r.name}</b>{r.alreadyEnrolled ? <div><span className="wau-pastilla acento">{S.yaInscrita}</span></div> : null}</td>
									<td style={{ fontSize: 13.5 }}>{r.courseName}</td>
									<td className="wau-num">{r.phone || "—"}</td>
									<td><BotonWhatsappAlumno campusId={campusId} studentId={r.studentId} nombre={r.name} /></td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}
			{masivo ? <EnvioMasivo campusId={campusId} studentIds={elegidas.map(r => r.studentId)} origen="taller" referencia={taller.name || null}
				fijosIniciales={fijos} onClose={() => setMasivo(false)} /> : null}
		</div>
	);
};

/** Pago a maestras del taller: horas de cada una, su forma de pago y lo que se le paga (solo roles de dinero). */
const PagoMaestras: React.FC<{ t: WorkshopMultiDetailDTO }> = ({ t }) => {
	const p = t.mentorPay;
	if (!p || p.rows.length === 0) return null;
	const tipo = (x: string | null) => x === "planta" ? S.planta : x === "hora" ? S.porHora : S.sinForma;
	return (
		<div className="wau-caja">
			<h3>{S.pagoMaestras}</h3>
			<div className="wau-tabla">
				<table className="wte-pago">
					<thead><tr><th>{S.maestra}</th><th>{S.formaPago}</th><th className="der">{S.horas}</th><th className="der">{S.tarifa}</th><th className="der">{S.aPagar}</th></tr></thead>
					<tbody>
						{p.rows.map(r => (
							<tr key={r.mentorId}>
								<td><b>{r.name}</b><div className={r.amount === null ? "wau-oro" : "wau-sec"} style={{ fontSize: 12.5 }}>{r.note}</div></td>
								<td>{p.hourlyRateOverride !== null ? S.tarifaHora : tipo(r.payType)}</td>
								<td className="der wau-num">{horasTexto(r.hours)}</td>
								<td className="der wau-num">{r.rate !== null ? dinero(t.currency, r.rate) : "—"}</td>
								<td className="der wau-num">{r.amount !== null ? dineroCod(t.currency, r.amount) : "—"}</td>
							</tr>
						))}
					</tbody>
					{p.rows.length > 1 ? <tfoot><tr><td colSpan={4}>{S.total}</td><td className="der wau-num">{dineroCod(t.currency, p.total)}</td></tr></tfoot> : null}
				</table>
			</div>
		</div>
	);
};

const unicos = (xs: (string | null)[]): string[] => xs.filter((x, i): x is string => !!x && xs.indexOf(x) === i);

/** Detalle de un taller: datos, inscritas (agregar, asistió, baja) e invitación. */
const WorkshopDetail: React.FC<Props> = ({ campusId, id, opciones, onBack, notify }) => {
	const http = HttpManager.getInstance();
	const [t, setT] = useState<WorkshopMultiDetailDTO | null>(null);
	const [editar, setEditar] = useState<boolean>(false);
	const rolDinero = useRolDinero(campusId);
	const [cobra, setCobra] = useState<boolean>(true);
	const [precio, setPrecio] = useState<string>("");
	const [motivo, setMotivo] = useState<string>("");
	const [baja, setBaja] = useState<WorkshopAttendeeDTO | null>(null);
	const [busy, setBusy] = useState<boolean>(false);

	const cargar = () => http.workshopsService.details(campusId, id)
		.then(r => { setT(r.data as WorkshopMultiDetailDTO); setPrecio(String(r.data.price ?? "")); setCobra((r.data.price || 0) > 0); })
		.catch(e => notify(errorText(e), false));
	useEffect(() => { cargar();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [campusId, id]);

	const vigentes = useMemo(() => (t?.attendees || []).filter(a => a.status === 1), [t]);
	if (!t) return <div className="wau-caja wau-sec">{S.cargando}</div>;
	const soloLectura = t.isHistoric || t.status === "anulado";
	const conSesiones = (t.sessions || []).length > 0;
	const ses = t.sessions || [];
	const horasTotal = ses.reduce((n, x) => n + horasEntre(x.startTime, x.endTime || ""), 0);
	// R2: precio distinto al del taller solo para roles de dinero y con motivo (queda en Auditoría)
	const precioDistinto = cobra && rolDinero && precio.trim() !== "" && !isNaN(Number(precio)) && Math.abs(Number(precio) - (t.price || 0)) > 0.004;

	const agregar = (s: { id: number; name: string }) => {
		setBusy(true);
		http.workshopsService.addAttendee(campusId, { workshopId: t.id, studentId: s.id, charge: cobra, price: cobra && rolDinero && precio !== "" ? Number(precio) : undefined, priceNote: precioDistinto ? motivo.trim() : undefined })
			.then(r => { setT(r.data as WorkshopMultiDetailDTO); notify(S.agregada(s.name)); })
			.catch(e => notify(errorText(e), false))
			.finally(() => setBusy(false));
	};
	const asistio = (a: WorkshopAttendeeDTO, v: boolean) => {
		setT(x => x ? { ...x, attendees: x.attendees.map(y => y.id === a.id ? { ...y, attended: v } : y) } : x);
		http.workshopsService.setAttendance(campusId, a.id, v).catch(e => { notify(errorText(e), false); cargar(); });
	};
	const cobro = (a: WorkshopAttendeeDTO) => {
		if (!a.charge) return <span className="wau-sec">{a.paid ? S.pagado : S.sinCobro}</span>;
		return <><div className="wau-num">{dinero(t.currency, a.charge.total)}</div>
			<div className={a.paid ? "wau-sec" : "wau-oro"} style={{ fontSize: 12.5 }}>{a.paid ? S.pagado : S.porPagar + " " + dinero(t.currency, a.charge.pending)}</div></>;
	};

	return (
		<div>
			<div className="wau-caja">
				<div className="wte-det-cab">
					<div className="tit"><h3>{t.name}</h3><EstadoPill status={t.status} label={t.statusLabel} /></div>
					<div className="acc">
						{t.isHistoric ? <span className="wau-sec">{S.historicoAviso}</span> : <button type="button" className="wau-btn s mini" onClick={() => setEditar(true)}>{S.editar}</button>}
						<BotonX onClick={onBack} />
					</div>
				</div>
				<div className="wte-datos">
					<div><span className="wau-lab">{S.inicio}</span><span className="wau-num">{fechaHoraAmPm(t.startsAt)}</span></div>
					{t.endsAt ? <div><span className="wau-lab">{S.fin}</span><span className="wau-num">{fechaHoraAmPm(t.endsAt)}</span></div> : null}
					{conSesiones ? <div><span className="wau-lab">{S.dias}</span><span className="wau-num">{S.nDias(ses.length)} · {horasTexto(horasTotal)}</span></div> : null}
					<div><span className="wau-lab">{S.cursos}</span>{(t.courses || []).map(c => cursoCorto(c.name) || c.name).join(", ") || cursoCorto(t.courseName) || t.area || "—"}</div>
					<div><span className="wau-lab">{S.aula}</span>{unicos(ses.map(x => x.classroomName)).join(", ") || t.classroomName || "—"}</div>
					<div><span className="wau-lab">{S.maestra}</span>{unicos(ses.map(x => x.mentorName)).join(", ") || t.mentorName || "—"}</div>
					<div><span className="wau-lab">{S.precio}</span><span className="wau-num">{dineroCod(t.currency, t.price)}</span></div>
					{t.mentorHourlyRate != null ? <div><span className="wau-lab">{S.tarifaHora}</span><span className="wau-num">{dineroCod(t.currency, t.mentorHourlyRate)}</span></div> : null}
					<div><span className="wau-lab">{S.inscritas}</span><span className={"wau-num" + (t.spotsLeft === 0 ? " wau-oro" : "")}>{vigentes.length}{t.capacity ? " / " + t.capacity : ""}</span></div>
				</div>
				{t.notes ? <p className="wau-sec" style={{ margin: "10px 0 0 0", fontSize: 13.5 }}>{t.notes}</p> : null}
			</div>

			{t.isHistoric && !conSesiones ? null : <Sesiones campusId={campusId} taller={t} opciones={opciones} soloLectura={soloLectura} onChange={setT} notify={notify} />}

			<div className="wau-caja">
				<h3>{S.inscritas} <span className="wau-sec wau-num">({vigentes.length})</span></h3>
				{soloLectura ? null : (
					<div style={{ marginBottom: 12 }}>
						<div className="wau-fila" style={{ marginBottom: 8 }}>
							<label className="wau-campo" style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
								<input type="checkbox" checked={cobra} onChange={e => setCobra(e.target.checked)} /><span>{S.seCobra}</span></label>
							{cobra ? (rolDinero
								? <label className="wau-campo" style={{ width: 150 }}><span className="wau-lab">{S.precio} ({t.currency?.code || ""})</span><input type="number" min={0} step="0.01" className="wau-entrada wau-num" aria-label={S.precio} value={precio} onChange={e => setPrecio(e.target.value)} /></label>
								: <div className="wau-campo"><span className="wau-lab">{S.precioTaller}</span><span className="wau-num">{dineroCod(t.currency, t.price)}</span></div>) : null}
							{precioDistinto ? <label className="wau-campo" style={{ flex: "1 1 220px" }}><span className="wau-lab">{S.motivoPrecio}</span><input className="wau-entrada" maxLength={255} value={motivo} onChange={e => setMotivo(e.target.value)} /></label> : null}
						</div>
						<BuscarAlumna campusId={campusId} onPick={agregar} disabled={busy || t.spotsLeft === 0 || (precioDistinto && motivo.trim().length < 2)} placeholder={S.buscarAlumna} boton={S.buscarBtn} />
					</div>
				)}
				<div className="wau-tabla">
					<table>
						<thead><tr><th>{S.colAlumna}</th><th>{S.colTel}</th><th>{S.colEstado}</th><th className="der">{S.colCobro}</th><th>{conSesiones ? S.colSesiones : S.colAsistio}</th><th>{S.colNota}</th><th></th></tr></thead>
						<tbody>
							{t.attendees.length === 0 ? <tr><td colSpan={7} className="wau-sec" style={{ textAlign: "center", padding: 18 }}>{S.sinInscritas}</td></tr> : t.attendees.map(a => (
								<tr key={a.id} style={a.status !== 1 ? { opacity: .6 } : undefined}>
									<td><b>{a.studentName}</b><div className="wau-sec" style={{ fontSize: 12.5 }}>{a.identityCode}</div></td>
									<td className="wau-num">{a.phone || "—"}</td>
									<td><span className={"wau-pastilla" + (a.status === 1 ? " acento" : "")}>{a.statusLabel}</span></td>
									<td className="der">{cobro(a)}</td>
									<td>{conSesiones
										? <ChipsSesiones campusId={campusId} taller={t} attendeeId={a.id} editable={!soloLectura && a.status === 1} onChange={setT} notify={notify} />
										: <input type="checkbox" checked={a.attended} disabled={soloLectura || a.status !== 1} aria-label={S.colAsistio} onChange={e => asistio(a, e.target.checked)} />}</td>
									<td style={{ fontSize: 13, maxWidth: 220 }}>{a.note || ""}{a.soldBy ? <div className="wau-sec">Vendió: {a.soldBy}</div> : ""}</td>
									<td style={{ textAlign: "right" }}>{!soloLectura && a.status === 1 ? <button type="button" className="wau-btn s mini" onClick={() => setBaja(a)}>{S.baja}</button> : null}</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			</div>

			{rolDinero ? <PagoMaestras t={t} /> : null}

			{soloLectura ? null : <Invitacion campusId={campusId} taller={t} opciones={opciones} notify={notify} onInscritas={cargar} />}

			{editar ? <WorkshopForm campusId={campusId} opciones={opciones} taller={t} onClose={() => setEditar(false)}
				onSaved={(_x, texto) => { setEditar(false); notify(texto); cargar(); }} /> : null}
			{baja ? <PromptModal title={S.bajaTitulo(baja.studentName)} label={S.bajaNota} confirm={S.baja} optional busy={busy} onCancel={() => setBaja(null)}
				onConfirm={(nota) => {
					setBusy(true);
					http.workshopsService.removeAttendee(campusId, baja.id, nota || undefined)
						.then(r => { setT(r.data as WorkshopMultiDetailDTO); setBaja(null); notify(S.bajaOk); })
						.catch(e => notify(errorText(e), false))
						.finally(() => setBusy(false));
				}} /> : null}
		</div>
	);
};

export default WorkshopDetail;
